import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { setBookings } from "@/slices/bookingsSlice";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  findAgefromDOB,
} from "@/util/common_function";
import { Form } from "antd";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derives the holdReasonId value from an inmateInfo object.
 * When sourced from charges:
 *   - controlling charge present  → 154530006
 *   - otherwise                   → 154530013
 * When sourced from arrestReports, use the stored holdReasonId (with same
 * controlling-charge override) or fall back to 154530013.
 */
const resolveHoldReasonId = (inmateInfo, usingCharges) => {
  if (usingCharges) {
    return inmateInfo.controllingCharge ? 154530006 : 154530013;
  }
  if (inmateInfo.holdReasonId) {
    return inmateInfo.controllingCharge ? 154530006 : inmateInfo.holdReasonId;
  }
  return 154530013;
};

/**
 * Picks the best controlling charge from the charges array:
 *   1. A controlling charge that has NOT been cleared.
 *   2. Any controlling charge (all cleared).
 *   3. undefined — no controlling charge exists.
 */
const pickControllingCharge = (charges = []) =>
  charges.find((c) => c.controllingCharge === true && c.clearedToGo !== true) ??
  charges.find((c) => c.controllingCharge === true);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const InmateInformation = forwardRef(
  ({ inmateInfoRef, startingPoint = "" }, ref) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings,
    );
    const fullSearch = useSelector((state) => state.fullSearch);

    // Keep a ref so the debounced callback always reads the latest bookings
    // without needing to be re-created on every render.
    const bookingsRef = useRef(bookings);
    useEffect(() => {
      bookingsRef.current = bookings;
    }, [bookings]);

    // -------------------------------------------------------------------------
    // Derive form values from bookings and sync into antd form
    // -------------------------------------------------------------------------
    useEffect(() => {
      let inmateInfo;
      let usingCharges = false;

      const chargeCandidate = pickControllingCharge(bookings?.charges);
      if (chargeCandidate) {
        inmateInfo = chargeCandidate;
        usingCharges = true;
      }

      // Merge arrest-report data underneath charges (charges take priority)
      if (bookings?.arrestReports?.length > 0) {
        inmateInfo = { ...bookings.arrestReports[0], ...inmateInfo };
      }

      let nextValues;

      if (inmateInfo) {
        nextValues = {
          holdReasonId: resolveHoldReasonId(inmateInfo, usingCharges),
          controllingCharge: usingCharges
            ? getRefListNameByID("Charges", inmateInfo?.chargeDescriptionID)
            : inmateInfo.controllingCharge,
          inmateStatusId: inmateInfo.inmateStatusId,
          workhouseStatusId: inmateInfo.workhouseStatusId,
          releaseToFacilityId: inmateInfo.releaseToFacilityId,
          communityServiceHoursSentenced:
            inmateInfo.communityServiceHoursSentenced,
          communityServiceHoursWorked: inmateInfo.communityServiceHoursWorked,
          inmateTypeId: inmateInfo.inmateTypeId,
          controllingAgencyCaseNumber: usingCharges
            ? inmateInfo.caseNumber
            : inmateInfo.controllingAgencyCaseNumber,
          controllingAgencyOriId: usingCharges
            ? inmateInfo.controllingAgencyORIID
            : inmateInfo.controllingAgencyOriId,
        };
      } else {
        nextValues = {
          holdReasonId: fullSearch ? null : 154530013,
          controllingCharge: null,
          inmateStatusId: null,
          workhouseStatusId: null,
          releaseToFacilityId: null,
          communityServiceHoursSentenced: null,
          communityServiceHoursWorked: null,
          inmateTypeId: null,
          controllingAgencyCaseNumber: null,
          controllingAgencyOriId: null,
        };
      }

      // Auto-set inmate type from DOB when charges haven't supplied one
      if (bookings?.bio?.[0]?.dob && !nextValues.inmateTypeId) {
        const age = findAgefromDOB(bookings.bio[0].dob);
        nextValues.inmateTypeId = age < 18 ? 154540005 : 154540001;
      }

      form.setFieldsValue(nextValues);
    }, [bookings, fullSearch, form]);

    // -------------------------------------------------------------------------
    // Expose form API to parent via ref
    // -------------------------------------------------------------------------
    useImperativeHandle(inmateInfoRef, () => ({
      validateFields: () => form.validateFields(),
      resetForm: () => form.resetFields(),
    }));

    // -------------------------------------------------------------------------
    // Debounced form → Redux sync (stable reference across renders)
    // -------------------------------------------------------------------------
    const updateStore = useCallback(
      debounce(() => {
        const values = form.getFieldsValue();
        dispatch(
          setBookings({
            arrestReports: [
              {
                ...bookingsRef.current?.arrestReports?.[0],
                ...values,
              },
            ],
          }),
        );
      }, 300),
      [form, dispatch], // debounce instance is recreated only if form/dispatch changes
    );

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    return (
      <FieldSecurityProvider
        entity="InmateInformation"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form
          form={form}
          disabled={!bookingsFieldsEnabled}
          onFieldsChange={updateStore}
        >
          <StyledGridRow gutter={16}>
            {/* Hold Reason */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="holdReasonId"
                formItemProps={{
                  name: "holdReasonId",
                  label: "Hold Reason",
                  required: !fullSearch,
                  rules: !fullSearch
                    ? [{ required: true, message: "Hold Reason is required" }]
                    : [],
                }}
              >
                <Select
                  id="IF-HoldReason"
                  options={getOptionsFromRefList(
                    "Inmate_Hold_Reason",
                    false,
                    true,
                    true,
                  )}
                  placeholder="Hold Reason"
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Inmate Status */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="inmateStatusId"
                formItemProps={{
                  name: "inmateStatusId",
                  label: "Inmate Status",
                }}
              >
                <Select
                  id="IF-InmateStatus"
                  options={getOptionsFromRefList(
                    "Inmate_Status",
                    false,
                    true,
                    true,
                  )}
                  placeholder="Inmate Status"
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Facility Status */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="workhouseStatusId"
                formItemProps={{
                  name: "workhouseStatusId",
                  label: "Facility Status",
                }}
              >
                <Select
                  id="IF-FacilityStatus"
                  options={getOptionsFromRefList(
                    "Workhouse_Status",
                    false,
                    true,
                    true,
                  )}
                  placeholder="Facility Status"
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          <StyledGridRow gutter={16}>
            {/* Community Service Hours Sentenced */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="communityServiceHoursSentenced"
                formItemProps={{
                  name: "communityServiceHoursSentenced",
                  label: "Comm. Svc. Hours Sentenced",
                }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </StyledGridCol>

            {/* Community Service Hours Worked */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="communityServiceHoursWorked"
                formItemProps={{
                  name: "communityServiceHoursWorked",
                  label: "Comm. Svc. Hours Worked",
                }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </StyledGridCol>

            {/* Inmate Type */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="inmateTypeId"
                formItemProps={{ name: "inmateTypeId", label: "Inmate Type" }}
              >
                <Select
                  id="IF-InmateType"
                  options={getOptionsFromRefList(
                    "Inmate_Type",
                    false,
                    false,
                    true,
                  )}
                  placeholder="Inmate Type"
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          <StyledGridRow gutter={16}>
            {/* Controlling Agency Case # — read-only when not in fullSearch mode */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="controllingAgencyCaseNumber"
                overrideDisabled={!fullSearch}
                formItemProps={{
                  name: "controllingAgencyCaseNumber",
                  label: "Controlling Agency Case #",
                }}
              >
                <TextField id="IF-ControllingAgencyCase" />
              </SecureFormItem>
            </StyledGridCol>

            {/* Controlling Agency ORI */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="controllingAgencyOriId"
                formItemProps={{
                  name: "controllingAgencyOriId",
                  label: "Controlling Agency ORI",
                }}
              >
                <Select
                  id="IF-ControllingAgencyORI"
                  options={getOptionsFromRefList(
                    "Controlling_Agency",
                    false,
                    true,
                    true,
                  )}
                  placeholder="Controlling Agency ORI"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Controlling Charge — read-only when not in fullSearch mode */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="controllingCharge"
                overrideDisabled={!fullSearch}
                formItemProps={{
                  name: "controllingCharge",
                  label: "Controlling Charge",
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>
    );
  },
);

export default InmateInformation;
